/**
 * Display-only short label for a line's charge type (Nahid, 2 Nov 2026).
 *
 * On the estimate tab and the negotiation popup the admin should see the vendor's charge type as a
 * short bucket — "Labor", "Helper" or "Trip" — instead of the long rate-card wording the vendor
 * actually submitted ("Standard Hourly Rate", "Overtime/ Weekend Flat Rate Trip Charge",
 * "Standard Hourly Rate(Tech 1)", ...).
 *
 * Mapping rules (from the sync):
 *   - Standard / Overtime / Emergency (etc.) hourly or labor rate  -> "Labor"
 *   - any Helper variant of the above                              -> "Helper"
 *   - Flat Trip / Emergency Flat Trip / Trip Charge                -> "Trip"
 *   - anything else (materials, misc. items)                       -> returned unchanged
 *
 * DISPLAY ONLY. Nothing here changes what is saved to the database, nor what the invoice or PDF
 * render — those keep the original charge-type name.
 *
 * The real strings are messy (tech suffixes, "X Tech on Site" tails, "Overtime/ Weekend ..."
 * prefixes), so matching is by keyword bucket rather than an exact table. Reconcile these rules
 * with Nahid's mapping chart when it arrives — adding/adjusting a bucket should be a one-line change.
 */

export type ChargeTypeBucket = 'Labor' | 'Helper' | 'Trip';

/**
 * Returns the short display label for a charge-type name, or the original name unchanged when it is
 * not a recognised labor/helper/trip charge type.
 *
 * @param name    the charge-type / item name as submitted (e.g. "Standard Hourly Rate").
 * @param isLabor optional hint: the line is flagged as labor (itemCode === 'LABOR' / isLabor). When
 *                set, an unrecognised-but-labor line still falls through to "Labor".
 */
export function chargeTypeDisplayLabel(
  name: string | null | undefined,
  isLabor = false,
): string {
  const raw = (name ?? '').trim();
  if (!raw) return raw;
  const n = raw.toLowerCase();

  // Trip first: a trip charge is "flat"/"emergency", never "hourly", so it must be caught before the
  // labor rules so an "Overtime/ Weekend Flat Rate Trip Charge" does not fall into Labor on "weekend".
  if (n.includes('trip')) return 'Trip';

  // Helper before Labor: a helper line also reads "hourly", so catch "helper" first.
  if (n.includes('helper')) return 'Helper';

  // Labor: any hourly / labor rate — standard, overtime, emergency, weekend, etc.
  if (isLabor || n.includes('hourly') || n.includes('labor') || n.includes('labour')) return 'Labor';

  // Not a recognised charge type (material / misc.) — leave it exactly as it was.
  return raw;
}
